/**
 * Pattern: Wolf Stack (Diaz / StockMarketWolf)
 *
 * Diaz's signature trading stack alignment check at the entry bar:
 *   9 EMA + 20 EMA + VWAP + MACD histogram
 *
 * For LONG: all four want to be "bullish-side":
 *   - 9 EMA > 20 EMA   (fast above slow)
 *   - Price > 9 EMA     (riding the trigger)
 *   - Price > VWAP      (above session value)
 *   - MACD histogram > 0 (momentum positive)
 *
 * For SHORT: mirror — fast below slow, price below 9 EMA, price below
 * VWAP, MACD histogram negative.
 *
 * Returns a score 0–4 of how many of the four agree with the trade
 * direction. Diaz's rule of thumb: take only 3+ alignments.
 *
 * Pure function. No side effects.
 */

import { computeEMA } from '../emas.js';
import { computeVWAP, vwapAtTime } from '../vwap.js';
import { computeMACD, macdAtTime } from '../macd.js';

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

export const detectWolfStack = ({ bars, entryTs, entryPrice, direction, options = {} } = {}) => {
  const dir = normalizeDirection(direction);
  if (!dir) return null;
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(entryTs) || !Number.isFinite(entryPrice)) return null;

  const fastPeriod = options.fastEma || 9;
  const slowPeriod = options.slowEma || 20;

  const entryIdx = findEntryBarIdx(bars, entryTs);
  if (entryIdx < 0) return null;

  const fastSeries = computeEMA(bars, fastPeriod);
  const slowSeries = computeEMA(bars, slowPeriod);
  const ema9 = fastSeries[entryIdx]?.value;
  const ema20 = slowSeries[entryIdx]?.value;

  let vwap = null;
  let macdHist = null;
  try {
    const vwapSeries = computeVWAP(bars);
    vwap = vwapAtTime(vwapSeries, entryTs);
  } catch { /* non-fatal */ }
  try {
    const macdSeries = computeMACD(bars);
    const m = macdAtTime(macdSeries, entryTs);
    macdHist = m?.histogram ?? null;
  } catch { /* non-fatal */ }

  // Score: each criterion that aligns with direction adds 1
  const checks = [];
  const align = dir === 'long' ? 1 : -1;

  if (Number.isFinite(ema9) && Number.isFinite(ema20)) {
    const fastVsSlow = (ema9 - ema20) * align;
    checks.push({ name: '9 EMA vs 20 EMA', pass: fastVsSlow > 0 });
  } else {
    checks.push({ name: '9 EMA vs 20 EMA', pass: false, missing: true });
  }

  if (Number.isFinite(ema9)) {
    const priceVsEma9 = (entryPrice - ema9) * align;
    checks.push({ name: 'Price vs 9 EMA', pass: priceVsEma9 > 0 });
  } else {
    checks.push({ name: 'Price vs 9 EMA', pass: false, missing: true });
  }

  if (Number.isFinite(vwap)) {
    const priceVsVwap = (entryPrice - vwap) * align;
    checks.push({ name: 'Price vs VWAP', pass: priceVsVwap > 0 });
  } else {
    checks.push({ name: 'Price vs VWAP', pass: false, missing: true });
  }

  if (Number.isFinite(macdHist)) {
    const macdAligned = macdHist * align;
    checks.push({ name: 'MACD histogram', pass: macdAligned > 0 });
  } else {
    checks.push({ name: 'MACD histogram', pass: false, missing: true });
  }

  const score = checks.filter(c => c.pass).length;
  const maxScore = checks.filter(c => !c.missing).length;
  if (maxScore === 0) return null;

  return {
    type: 'wolf-stack',
    score,
    maxScore,
    aligned: score >= 3,
    details: checks.map(c =>
      c.missing ? `${c.name}: missing data` : `${c.name}: ${c.pass ? '✓' : '✗'}`
    ).join(' · '),
    evidence: {
      ema9, ema20, vwap, macdHist,
      checks,
    },
  };
};
